import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  listPresentations,
  createPresentation,
  deletePresentation
} from '../services/presentationsService';
import { listMyViewsForPresentations } from '../services/presentationViewsService';
import { getSlideCountsByPresentationIds } from '../services/presentationSlidesService';
import { listEvents } from '../services/eventsService';
import toast from 'react-hot-toast';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiPlay, FiEdit, FiTrash2, FiFolder, FiImage } = FiIcons;

const TYPE_LABELS = { slides: 'Slides', pdf: 'PDF', embed: 'Embed', mixed: 'Mixed' };
const TYPE_BADGE = {
  slides: 'bg-blue-100 text-blue-800',
  pdf: 'bg-amber-100 text-amber-800',
  embed: 'bg-purple-100 text-purple-800',
  mixed: 'bg-slate-100 text-slate-800'
};

export default function PresentationsList() {
  const { profile, authReady, loading: authLoading, profileLoading } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [eventIdFilter, setEventIdFilter] = useState('');
  const [presentations, setPresentations] = useState([]);
  const [events, setEvents] = useState([]);
  const [viewsByPres, setViewsByPres] = useState({});
  const [slideCounts, setSlideCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!profile?.id || !profile?.role || (profile.role !== 'agent' && profile.role !== 'admin')) return;

    setLoading(true);
    try {
      const opts = { role: profile.role, userId: profile.id };
      let listOpts = { ...opts };
      if (filter === 'standalone') listOpts.standaloneOnly = true;
      if (filter === 'by_event' && eventIdFilter) listOpts.eventId = eventIdFilter;

      const { data: list, error: listErr } = await listPresentations(listOpts);
      if (listErr) {
        toast.error(listErr.message || 'Failed to load presentations');
        setPresentations([]);
        return;
      }

      const items = list || [];
      setPresentations(items);

      const ids = items.map((p) => p.id);
      if (ids.length > 0) {
        const [viewsRes, countsRes] = await Promise.all([
          listMyViewsForPresentations(ids, opts),
          getSlideCountsByPresentationIds(ids, opts)
        ]);
        const viewMap = {};
        (viewsRes.data || []).forEach((v) => {
          viewMap[v.presentation_id] = v;
        });
        setViewsByPres(viewMap);
        setSlideCounts(countsRes.data || {});
      } else {
        setViewsByPres({});
        setSlideCounts({});
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load presentations');
      setPresentations([]);
    } finally {
      setLoading(false);
    }
  }, [profile?.id, profile?.role, filter, eventIdFilter]);

  useEffect(() => {
    if (!authReady || authLoading || profileLoading || !profile?.id) return;
    if (profile.role !== 'agent' && profile.role !== 'admin') return;
    load();
  }, [authReady, authLoading, profileLoading, profile?.id, profile?.role, load]);

  useEffect(() => {
    if ((profile?.role !== 'agent' && profile?.role !== 'admin') || !profile?.id) return;
    listEvents({ role: profile.role, userId: profile.id }).then(({ data: evs }) => {
      setEvents(evs || []);
    });
  }, [profile?.id, profile?.role]);

  const handleCreateNew = async () => {
    setCreating(true);
    try {
      const { data, error } = await createPresentation(
        { title: 'Untitled Presentation', type: 'slides' },
        { role: profile.role, userId: profile.id }
      );
      if (error) throw new Error(error.message);
      if (data?.id) navigate(`/presentations/${data.id}/edit`);
      else toast.error('Could not create presentation');
    } catch (e) {
      toast.error(e?.message || 'Failed to create presentation');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this presentation? This cannot be undone.')) return;
    setDeleting(id);
    try {
      const { error } = await deletePresentation(id, { role: profile.role, userId: profile.id });
      if (error) throw new Error(error.message);
      toast.success('Presentation deleted');
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  if (!authReady || authLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00AAFF] border-t-transparent" />
      </div>
    );
  }

  if (profile?.role !== 'agent' && profile?.role !== 'admin') {
    return (
      <div className="p-6 text-center text-gray-600">
        You don’t have access to presentations.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Presentations</h1>
          <p className="text-gray-600 mt-1">Create and manage slide decks and media</p>
        </div>
        <button
          type="button"
          onClick={handleCreateNew}
          disabled={creating}
          className="inline-flex items-center justify-center px-4 py-2 bg-[#00AAFF] text-white rounded-xl hover:bg-[#0090dd] transition-colors disabled:opacity-50"
        >
          {creating ? (
            <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
          ) : (
            <>
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              New Presentation
            </>
          )}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-2">
          {['all', 'standalone', 'by_event'].map((key) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === key
                  ? 'bg-[#00AAFF]/15 text-[#00AAFF]'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {key === 'all' ? 'All' : key === 'standalone' ? 'Standalone' : 'By Event'}
            </button>
          ))}
          {filter === 'by_event' && (
            <select
              value={eventIdFilter}
              onChange={(e) => setEventIdFilter(e.target.value)}
              className="ml-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Select event…</option>
              {(events || []).map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00AAFF] border-t-transparent" />
          </div>
        ) : presentations.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            No presentations yet. Create one to get started.
          </div>
        ) : (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {presentations.map((p) => {
              const view = viewsByPres[p.id];
              const total = slideCounts[p.id] ?? 0;
              const current = view?.last_slide_viewed ?? 0;
              const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;

              return (
                <div
                  key={p.id}
                  className="rounded-xl border border-gray-200 overflow-hidden bg-white hover:shadow-md transition-shadow"
                >
                  <Link to={`/presentations/${p.id}/view`} className="block aspect-video bg-gray-100 relative">
                    {p.thumbnail_url ? (
                      <img
                        src={p.thumbnail_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <SafeIcon icon={FiImage} className="w-12 h-12" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center">
                      <span className="opacity-0 hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2">
                        <SafeIcon icon={FiPlay} className="w-5 h-5 text-gray-800" />
                      </span>
                    </div>
                  </Link>
                  <div className="p-3">
                    <h3 className="font-semibold text-gray-900 truncate" title={p.title}>
                      {p.title || 'Untitled'}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${TYPE_BADGE[p.type] || TYPE_BADGE.mixed}`}>
                        {TYPE_LABELS[p.type] || p.type}
                      </span>
                      {p.event_id && (
                        <span className="text-xs text-gray-500 flex items-center">
                          <SafeIcon icon={FiFolder} className="w-3 h-3 mr-1" />
                          Event
                        </span>
                      )}
                    </div>
                    {total > 0 && (
                      <div className="mt-2">
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#00AAFF] rounded-full transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {current} / {total} slides
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="px-3 pb-3 flex items-center gap-2">
                    <Link
                      to={`/presentations/${p.id}/view`}
                      className="flex-1 text-center py-2 text-sm text-[#00AAFF] hover:bg-[#00AAFF]/10 rounded-lg"
                    >
                      View
                    </Link>
                    <Link
                      to={`/presentations/${p.id}/edit`}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                      title="Edit"
                    >
                      <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    </Link>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); handleDelete(p.id); }}
                      disabled={deleting === p.id}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                      title="Delete"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
